import { Box } from '@mui/system';
import GeneralStripePaymentForm from '../forms/apps/GeneralStripePaymentForm';
import { useState, useEffect } from 'react';
import CallcenterSetup from '@components/apps/callcenter-tool/setup.jsx';
import TodoSetup from '@components/apps/todo-tool/setup.jsx';
import CallbackSetup from '@components/apps/callback-tool/setup.jsx';
import WareHouseSetup from './warehouse-tool/setup';
import Tanss2WeclappSetup from './tanss2weclapp/setup';
import PassSetup from './pass-tool/setup';
import PMASetup from './pma/setup';
import PMAInitialEdit from './pma/initialEdit';
import PMAEdit from './pma/edit';
import StandaloneDialog from './StandaloneDialog';
import { useNavigate } from 'react-router-dom';
import tenant from '@/services/TenantService';
import { toast } from 'react-toastify';
import ToastMessage from '../toasts/ToastMessage';
import { Skeleton } from '@mui/material';
import CrmSetup from './crm/setup';

export default function AppInstaller({ app, onCancel, onFinish }) {
  const [setupFinished, setSetupFinished] = useState(false);
  const [users, setUsers] = useState([]);
  const [deployment, setDeployment] = useState(null);
  const [showInitialPMAEdit, setShowInitialPMAEdit] = useState(false);
  const [showPMAEdit, setShowPMAEdit] = useState(false);
  const [showStandaloneDialog, setShowStandaloneDialog] = useState(false);
  const [token, setToken] = useState(null);
  const [appIsPayed, setAppIsPayed] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const determineSetupNeeded = () => {
      switch (app.name) {
        case 'todo-tool':
        case 'callcenter-tool':
        case 'callback-tool':
        case 'pass-tool':
        case 'warehousenew':
        case 'pma':
        case 'tanss2weclapp':
        case 'crm':
          break;
        default:
          // All other apps don't have setup, so proceed directly to payment
          setSetupFinished(true);
      }
    };

    determineSetupNeeded();
  }, [app.name]);

  const handlePMASetupFinish = (deployment, token) => {
    setDeployment(deployment);
    setToken(token);
    setSetupFinished(true);
    handleSetupFinish();
  };

  const handleSetupFinish = () => {
    if (app.name === 'pma') {
      setShowInitialPMAEdit(true);
    }
    onFinish();
    navigate(`/apps/${app.name}`);
  };

  const renderSetup = () => {
    switch (app.name) {
      case 'todo-tool':
        return (
          <TodoSetup
            app={app}
            onCancel={onCancel}
            onFinish={(deployment, users) => {
              setUsers(users);
              setDeployment(deployment);
              setSetupFinished(true);
              handleSetupFinish();
            }}
            deployment={deployment}
          />
        );
      case 'callcenter-tool':
        return (
          <CallcenterSetup
            app={app}
            onCancel={onCancel}
            onFinish={(deployment, users) => {
              setUsers(users);
              setDeployment(deployment);
              setSetupFinished(true);
              handleSetupFinish();
            }}
            deployment={deployment}
          />
        );
      case 'callback-tool':
        return (
          <CallbackSetup
            app={app}
            onCancel={onCancel}
            onFinish={(deployment, users) => {
              setUsers(users);
              setDeployment(deployment);
              setSetupFinished(true);
              handleSetupFinish();
            }}
            deployment={deployment}
          />
        );
      case 'pass-tool':
        return (
          <PassSetup
            app={app}
            onCancel={onCancel}
            deployment={deployment}
            onFinish={(deployment, users) => {
              setUsers(users);
              setDeployment(deployment);
              setSetupFinished(true);
              handleSetupFinish();
            }}
          />
        );
      case 'warehousenew':
        return (
          <WareHouseSetup
            app={app}
            onCancel={onCancel}
            onFinish={(deployment, users) => {
              setUsers(users);
              setDeployment(deployment);
              setSetupFinished(true);
              handleSetupFinish();
            }}
            deployment={deployment}
          />
        );
      case 'pma':
        return (
          <PMASetup
            app={app}
            onCancel={onCancel}
            deployment={deployment}
            onFinish={handlePMASetupFinish}
          />
        );

      case 'tanss2weclapp':
        return (
          <Tanss2WeclappSetup
            app={app}
            deployment={deployment}
            onCancel={onCancel}
            onFinish={() => {
              handleSetupFinish();
            }}
          />
        );
      case 'crm':
        return (
          <CrmSetup
            app={app}
            deployment={deployment}
            onCancel={onCancel}
            onFinish={() => {
              handleSetupFinish();
            }}
          />
        );
      default:
        return null;
    }
  };

  const renderPayment = (deployment) => {
    return (
      <Box
        sx={{
          p: 3,
          overflowY: 'auto',
          flexGrow: 1,
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          height: '100%',
          justifyContent: 'center',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <GeneralStripePaymentForm
          app={app}
          users={users}
          deploymentId={deployment.insertedId}
          onFinish={() => setAppIsPayed(true)}
          onCancel={onCancel}
        />
      </Box>
    );
  };

  const createEmptyDeployment = async () => {
    const deploymentData = {
      app: app._id,
      expiresAt: new Date(Date.now() + 10 * 60 * 1000),
    };
    try {
      const deploymentResponse = await tenant.post(
        '/deployment',
        deploymentData,
      );
      const { deployment: deploymentReturn } = deploymentResponse.data;

      setDeployment(deploymentReturn);
      return deploymentReturn;
    } catch (error) {
      toast.error(
        <ToastMessage
          title="Fehler"
          subtitle={
            error?.response?.data?.message ||
            'Fehler beim Erstellen der Konfiguration'
          }
        />,
      );
    }
  };

  useEffect(() => {
    if (!appIsPayed && !deployment) {
      createEmptyDeployment().then((dep) => {
        setDeployment(dep);
      });
    }
  }, [appIsPayed, deployment]);

  if (!appIsPayed) {
    const dep = deployment;
    if (!dep) {
      // Optionally show a loading spinner here
      //
      return (
        <Box
          sx={{
            p: 5,
            display: 'flex',
            flex: 1,
            height: '100%',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          <Skeleton variant="rectangular" width={'100%'} height={180} />
          <Skeleton variant="rectangular" width={'100%'} height={100} />
          <Skeleton variant="rectangular" width={'100%'} height={100} />
        </Box>
      );
    }
    return renderPayment(dep);
  }

  if (!setupFinished && appIsPayed) {
    return renderSetup();
  }

  if (showInitialPMAEdit) {
    return (
      <PMAInitialEdit
        deployment={deployment}
        token={token}
        onCancel={onCancel}
        onFinish={() => {
          setShowInitialPMAEdit(false);
          setShowPMAEdit(true);
        }}
      />
    );
  }

  if (showPMAEdit) {
    return (
      <PMAEdit
        deployment={deployment}
        onCancel={onCancel}
        onFinish={() => {
          setShowPMAEdit(false);
          navigate(`/apps/${app.name}`);
        }}
      />
    );
  }

  if (showStandaloneDialog) {
    return (
      <StandaloneDialog
        open={showStandaloneDialog}
        onClose={() => {
          setShowStandaloneDialog(false);
          navigate(`/apps/${app.name}`);
        }}
        deployment={deployment}
        onFinish={() => {
          setShowStandaloneDialog(false);
          navigate(`/apps/${app.name}`);
        }}
      />
    );
  }
}
